import React from 'react';
import { PageHero, BottomCTA } from '../components/PageHelpers';
import { Sec, SH } from '../components/Section';
import { Btn, Crd } from '../components/Button';
import { BulletList } from '../components/AnimatedComponents';
import { MockupGraphic } from '../components/MockupGraphic';
import { C } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { motion } from 'framer-motion';
import { L } from '../router/Router';

// Use Cases Page - Exact original content
export const UseCasesPage = () => {
  const { theme } = useTheme();

  const useCases = [
    {
      icon: '🚀',
      title: 'Founders Raising Capital',
      subtitle: 'Pitch Decks for Investors',
      desc: "Stop guessing why investors aren't responding. Get your DecX Score, fix what's broken, close your round faster.",
      features: ['DecX Score', 'Investor Audience Insights', 'Outreach Sequences', '1:1 Strategy Calls'],
      link: '/for-founders'
    },
    {
      icon: '💼',
      title: 'B2B Sales Teams',
      subtitle: 'Sales Decks for Buyers',
      desc: "Find out which slides are killing your close rate. Transform your sales deck into a conversion machine.",
      features: ['DecX Score', 'Buyer Persona Mapping', 'Competitor Battle Cards', 'Follow-Up Templates'],
      link: '/for-sales-teams'
    },
    {
      icon: '📋',
      title: 'Product Managers',
      subtitle: 'Product Decks for Stakeholders',
      desc: "See why executives tune out — and how to fix it. Get budget approvals faster with decks that land.",
      features: ['DecX Score', 'Executive Audience Insights', 'ROI Narrative Framework', 'Decision-Enabling Structure'],
      link: '/for-product-managers'
    },
    {
      icon: '🤝',
      title: 'Accelerators & VCs',
      subtitle: 'Portfolio Deck Scoring',
      desc: "Score entire cohorts. White-label reports. Track improvement over time.",
      features: ['Bulk Deck Scoring', 'Portfolio Benchmarking', 'White-Label Reports', 'API Access'],
      link: '/for-accelerators'
    }
  ];

  return (
    <div data-testid="use-cases-page">
      <PageHero
        eyebrow="USE CASES"
        title="One Platform."
        accent="Every Deck That Matters."
        subtitle="Whether you're raising capital, closing deals, or winning approvals."
        cta="Get Free Score"
        ctaLink="/score"
      />

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {useCases.map((uc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Crd glow={i === 0} data-testid={`use-case-detail-${i}`}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '40px', marginBottom: '16px' }}>{uc.icon}</div>
                      <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', color: C.secondary, marginBottom: '8px' }}>
                        {uc.subtitle}
                      </div>
                      <h3 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '16px', fontFamily: "'Sansation', sans-serif" }}>
                        {uc.title}
                      </h3>
                      <p className="font-[Manrope]" style={{ fontSize: '16px', lineHeight: 1.6, opacity: 0.8, marginBottom: '24px' }}>
                        {uc.desc}
                      </p>
                      <Btn variant="primary" to={uc.link}>
                        Learn More
                      </Btn>
                    </div>
                    <div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                        {uc.features.map((f, j) => (
                          <span
                            key={j}
                            style={{
                              padding: '8px 16px',
                              background: 'rgba(255,255,255,0.05)',
                              border: `1px solid ${C.secondary}30`,
                              borderRadius: '100px',
                              fontSize: '13px',
                              fontWeight: 600
                            }}
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Crd>
              </motion.div>
            ))}
          </div>
        </div>
      </Sec>

      <BottomCTA
        title="Ready to Transform Your Deck"
        accent="to DecX?"
        subtitle="Start free. Get your score. Close your deal."
        ctaText="Get Free DecX Score"
        ctaLink="/score"
      />
    </div>
  );
};

// Intelligence Page - Exact original content
export const IntelligencePage = () => {
  const { theme } = useTheme();

  const modules = [
    { icon: '📊', title: 'DecX Score & Analysis', desc: 'Know where your deck stands across 5 dimensions.' },
    { icon: '🔎', title: 'Unexplored Intelligence', desc: "Discover gaps you didn't know existed." },
    { icon: '👤', title: 'Audience Insights', desc: "Understand who you're pitching and what they need." },
    { icon: '📈', title: 'Industry Trends', desc: "See what's working in your sector." }
  ];

  const subpages = [
    { title: 'Pitch Deck Intelligence', link: '/pitch-deck-intelligence' },
    { title: 'Sales Deck Intelligence', link: '/sales-deck-intelligence' },
    { title: 'Product Deck Intelligence', link: '/product-deck-intelligence' }
  ];

  return (
    <div data-testid="intelligence-page">
      <PageHero
        eyebrow="INTELLIGENCE SUITE"
        title="Deep Deck Intelligence"
        accent="Powered by AI"
        subtitle="Know where you stand. Know your audience. Know your market."
        cta="Get Free Score"
        ctaLink="/score"
      />

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <SH
            eyebrow="INTELLIGENCE MODULES"
            title="Four Pillars of"
            accent="Deck Intelligence"
            center
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px', marginTop: '48px' }}>
            {modules.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Crd data-testid={`intel-module-${i}`}>
                  <div style={{ fontSize: '40px', marginBottom: '16px' }}>{m.icon}</div>
                  <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '10px', fontFamily: "'Sansation', sans-serif" }}>
                    {m.title}
                  </h3>
                  <p className="font-[Manrope]" style={{ fontSize: '14px', lineHeight: 1.6, opacity: 0.75 }}>
                    {m.desc}
                  </p>
                </Crd>
              </motion.div>
            ))}
          </div>

          <div style={{ marginTop: '64px', textAlign: 'center' }}>
            <h3 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '24px', fontFamily: "'Sansation', sans-serif" }}>
              Explore Intelligence Types
            </h3>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
              {subpages.map((sp, i) => (
                <L key={i} to={sp.link}>
                  <Btn variant="outline">{sp.title}</Btn>
                </L>
              ))}
            </div>
          </div>
        </div>
      </Sec>

      <BottomCTA
        title="Ready to Transform Your Deck"
        accent="to DecX?"
        subtitle="Start free. Get your score. Close your deal."
        ctaText="Get Free DecX Score"
        ctaLink="/score"
      />
    </div>
  );
};

// Pricing Page - Exact original content
export const PricingPage = () => {
  const { PricingWidget, FAQWidget } = require('../widgets');

  return (
    <div data-testid="pricing-page">
      <PageHero
        eyebrow="PRICING"
        title="Start Free."
        accent="Upgrade When It Matters."
        subtitle="Full deck intelligence for a single payment."
      />

      <PricingWidget />
      <FAQWidget />
    </div>
  );
};

// Slidr Page - Exact original content
export const SlidrPage = () => {
  const { theme } = useTheme();

  const process = [
    { num: '01', title: 'Discovery', desc: 'Deep dive into your business, market, and audience.' },
    { num: '02', title: 'Strategy', desc: 'Positioning, narrative arc, and story structure.' },
    { num: '03', title: 'Design', desc: 'World-class visual design that matches your brand.' },
    { num: '04', title: 'Delivery', desc: 'Final deck + coaching on how to present it.' }
  ];

  return (
    <div data-testid="slidr-page">
      <PageHero
        eyebrow="DONE FOR YOU"
        title="Need Us to Build"
        accent="Your Deck?"
        subtitle="Slidr — Strategy. Narrative. Design. Delivery."
        body="Starting at $779."
        cta="Inquire Now"
        ctaLink="/contact"
      >
        <MockupGraphic type="artefacts" label="Slidr Deck Preview" />
      </PageHero>

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <SH
            eyebrow="THE PROCESS"
            title="How Slidr"
            accent="Works"
            center
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginTop: '48px' }}>
            {process.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Crd data-testid={`process-step-${i}`}>
                  <div style={{ fontSize: '40px', fontWeight: 700, background: C.grad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '16px' }}>
                    {p.num}
                  </div>
                  <h4 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '10px', fontFamily: "'Sansation', sans-serif" }}>
                    {p.title}
                  </h4>
                  <p className="font-[Manrope]" style={{ fontSize: '14px', lineHeight: 1.6, opacity: 0.75 }}>
                    {p.desc}
                  </p>
                </Crd>
              </motion.div>
            ))}
          </div>
        </div>
      </Sec>

      <BottomCTA
        title="Ready to Get Started?"
        subtitle="Let's build a deck that wins."
        ctaText="Contact Us"
        ctaLink="/contact"
      />
    </div>
  );
};

// Legal Pages - Exact original content
export const PrivacyPage = () => {
  const { theme } = useTheme();

  const sections = [
    {
      title: "1. INTRODUCTION",
      content: `Decks N Dollars LLP ("Company," "we," "us," or "our") operates the DecX AI (previously MyDeckAudit platform ("Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.

By using our Service, you consent to the data practices described in this Privacy Policy.`
    },
    {
      title: "2. INFORMATION WE COLLECT",
      content: `2.1 Information You Provide Directly

Account Information:
– Name, email address, phone number
– Company name and position
– Payment information (processed by third-party providers)
– Profile information and preferences

User Content:
– Slides, Decks and presentation materials
– Business information and financial data
– Communications with our support team
– Feedback and survey responses

2.2 Information Collected Automatically

Technical Information:
– IP address and geographic location
– Browser type and version
– Device information and operating system
– Log files and usage data
– Cookies and tracking technologies

Usage Analytics:
– Pages visited and features used
– Time spent on the platform
– Click patterns and user interactions
– Performance metrics and error reports

2.3 Information from Third Parties
– Payment processor transaction data
– Social media profile information (if you choose to connect)
– Third-party integrations and APIs
– Publicly available business information`
    },
    {
      title: "3. HOW WE USE YOUR INFORMATION",
      content: `3.1 Primary Purposes

Service Delivery:
– Providing deck analysis and reports
– Calculating DecX scores and recommendations
– Delivering analysis reports and communications
– Managing your account and preferences

Business Operations:
– Processing payments and billing
– Customer support and communications
– Platform maintenance and improvements
– Security monitoring and fraud prevention

3.2 Secondary Purposes

Analytics and Improvement:
– Analyzing usage patterns to improve our Service
– Conducting research and development
– Creating anonymized benchmarks and insights
– Generating industry reports and trends

Marketing and Communications:
– Sending service updates and notifications
– Marketing communications (with consent)
– Newsletter and educational content
– Customer success and onboarding`
    },
    {
      title: "4. LEGAL BASIS FOR PROCESSING (GDPR COMPLIANCE)",
      content: `We process your personal data based on the following legal grounds:

– Contract Performance: To provide our Service and fulfill our obligations
– Legitimate Interests: For business operations, security, and improvements
– Consent: For marketing communications and optional features
– Legal Compliance: To comply with applicable laws and regulations`
    },
    {
      title: "5. INFORMATION SHARING AND DISCLOSURE",
      content: `5.1 We DO NOT Sell Your Information
We do not sell, rent, or trade your personal information to third parties for their commercial purposes.

5.2 Authorized Disclosures

Service Providers:
– Payment processors for transaction handling
– Cloud hosting providers for data storage
– Analytics providers for usage insights
– Communication service providers

Legal Requirements:
– When required by law or legal process
– To protect our rights and property
– To prevent fraud or security threats
– In connection with business transfers

5.3 Anonymized Data
We may share anonymized, aggregated data that cannot identify individual users for:
– Industry benchmarking
– Research and development`
    }
  ];

  return (
    <div data-testid="privacy-page">
      <PageHero eyebrow="LEGAL" title="Privacy" accent="Policy" />
      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Last Updated */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '40px',
            fontSize: '14px',
            opacity: 0.7,
            fontFamily: "'Manrope', sans-serif"
          }}>
            Last Updated: 04-Mar-2026
          </div>

          {/* Privacy Sections */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {sections.map((section, index) => (
              <Crd key={index} style={{ padding: '28px 32px' }}>
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: 700, 
                  marginBottom: '16px', 
                  fontFamily: "'Sansation', sans-serif",
                  color: C.secondary
                }}>
                  {section.title}
                </h3>
                <div 
                  className="font-[Manrope]" 
                  style={{ 
                    lineHeight: 1.8, 
                    fontSize: '14px',
                    whiteSpace: 'pre-line',
                    opacity: 0.85
                  }}
                >
                  {section.content}
                </div>
              </Crd>
            ))}
          </div>
        </div>
      </Sec>
    </div>
  );
};

export const TermsPage = () => {
  const { theme } = useTheme();

  const sections = [
    {
      title: "1. INTRODUCTION AND ACCEPTANCE",
      content: `These Terms and Conditions ("Terms") govern your use of the DecX AI (previously MyDeckAudit) platform and Platform/Services ("Platform/Service") operated by Decks N Dollars LLP, a company incorporated under the Companies Act, 2013, having its registered office at [Complete Registered Address], India ("Company," "we," "us," or "our").

By accessing or using our Platform/Service, you ("User," "you," or "your") agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access the Platform/Service.`
    },
    {
      title: "2. DEFINITIONS",
      content: `"Platform/Service" means the MyDeckAudit platform, including the website, mobile applications, and all related Platform/Services provided by the Company.

"Content" means all text, data, information, software, graphics, photographs, and other materials uploaded, downloaded, or appearing on the Platform/Service.

"User Content" means any Content that you upload, submit, or transmit through the Platform/Service, including pitch decks, presentations, and related materials.

"Analysis Report" means the comprehensive evaluation and recommendations provided by our Platform/Service regarding your pitch deck.

"ILTC Score" means our proprietary Investment-Level Traction Credibility scoring methodology.`
    },
    {
      title: "3. DESCRIPTION OF Platform/Service",
      content: `DecX AI (previously MyDeckAudit) provides professional deck analysis & Intelligence Platform and Platform/Services through our proprietary DecX scoring system. Our Platform/Service includes:

– Comprehensive deck analysis across five key dimensions
– Slide-by-slide feedback and recommendations
– Industry benchmarking and competitive analysis
– Professional reports and documentation
– Dashboard access for tracking progress
– and more..`
    },
    {
      title: "4. ELIGIBILITY AND REGISTRATION",
      content: `4.1 Age Requirements
You must be at least 18 years old to use our Platform/Platform/Service. By using the Platform/Platform/Service, you represent and warrant that you meet this age requirement.

4.2 Account Registration
To access certain features, you must create an account by providing accurate, current, and complete information. You are responsible for:
– Maintaining the confidentiality of your account credentials
– All activities that occur under your account
– Immediately notifying us of any unauthorized use

4.3 Corporate Users
If you are using the Platform/Service on behalf of a company or organization, you represent that you have the authority to bind that entity to these Terms.`
    },
    {
      title: "5. USE OF Platform/Service",
      content: `5.1 License Grant
Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Platform/Service for your business purposes.

5.2 Acceptable Use
You agree to use the Platform/Service only for lawful purposes and in accordance with these Terms. You shall not:
– Use the Platform/Service for any illegal or unauthorized purpose
– Violate any applicable laws or regulations
– Infringe upon the rights of others
– Upload malicious code, viruses, or harmful content
– Attempt to gain unauthorized access to our systems
– Reverse engineer or attempt to extract source code
– Use automated tools to access the Platform/Service without permission
– Resell or redistribute our Platform/Service without authorization

5.3 Prohibited Content
You shall not upload or submit:
– Copyrighted material without authorization
– Confidential information of third parties without consent
– False, misleading, or fraudulent information
– Content that violates any applicable laws
– Personally identifiable information of third parties without consent`
    },
    {
      title: "6. INTELLECTUAL PROPERTY RIGHTS",
      content: `6.1 Our Intellectual Property
The Platform/Platform/Service, including all software, text, images, trademarks, Platform/Service marks, logos, and other content, is owned by Decks N Dollars LLP or our licensors and is protected by Indian and international intellectual property laws.

6.2 Your Content
You retain ownership of your User Content. By uploading User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and analyze such content solely for the purpose of providing our Platform/Service.

6.3 Analysis Reports
Our Analysis Reports, methodology, and ILTC scoring system are proprietary to Decks N Dollars LLP. You may use the reports for your business purposes but may not redistribute or commercialize them without our written consent.`
    },
    {
      title: "7. PAYMENT TERMS",
      content: `7.1 Pricing
Current pricing for our Platform/Service is displayed on our website. All prices are in Indian Rupees (INR) or US Dollars (USD) as specified and are subject to applicable taxes.

7.2 Payment Processing
Payments are processed through secure third-party payment processors. By providing payment information, you authorize us to charge the applicable fees.

7.3 Refund Policy
We don't have any money-back guarantee for our Professional Audit Platform/Service.

7.4 Auto-Renewal
Subscription Platform/Services automatically renew unless cancelled at least 24 hours before the renewal date. You may cancel subscriptions through your account settings.`
    },
    {
      title: "8. CONFIDENTIALITY AND DATA SECURITY",
      content: `8.1 Confidentiality Commitment
We understand the sensitive nature of pitch deck information and commit to maintaining strict confidentiality of all User Content and analysis results.

8.2 Data Security Measures
We implement industry-standard security measures including:
– 256-bit SSL encryption for data transmission
– Secure data storage with regular backups
– Access controls and authentication protocols
– Regular security audits and assessments

8.3 Employee Obligations
All our employees and contractors sign comprehensive non-disclosure agreements and undergo background checks before accessing user data.`
    },
    {
      title: "9. DISCLAIMERS AND LIMITATIONS",
      content: `9.1 Platform/Service Availability
We strive to maintain Platform/Service availability but do not guarantee uninterrupted access. We may temporarily suspend the Platform/Service for maintenance, updates, or other operational requirements.

9.2 Analysis Accuracy
While we provide professional analysis based on industry best practices, our recommendations are opinions and should not be considered as guarantees of funding success or investment outcomes.

9.3 No Investment Advice
Our Platform/Service provides pitch deck analysis and is not intended as investment advice, financial advice, or guarantees of funding success.`
    },
    {
      title: "10. LIMITATION OF LIABILITY",
      content: `10.1 Limitation of Damages
TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL DECKS N DOLLARS LLP BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.

10.2 Maximum Liability
OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE Platform/Service SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.

10.3 Jurisdictional Variations
Some jurisdictions do not allow the exclusion or limitation of certain warranties or damages. In such jurisdictions, our liability shall be limited to the maximum extent permitted by law.`
    },
    {
      title: "11. INDEMNIFICATION",
      content: `You agree to defend, indemnify, and hold harmless Decks N Dollars LLP, its officers, directors, employees, and agents from and against any claims, damages, obligations, losses, liabilities, costs, or debt arising from:
– Your use of the Platform/Service
– Your violation of these Terms
– Your User Content
– Your violation of any third-party rights`
    },
    {
      title: "12. TERMINATION",
      content: `12.1 Termination by You
You may terminate your account at any time by contacting our customer support or through your account settings.

12.2 Termination by Us
We may terminate or suspend your access immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users or our business interests.

12.3 Effect of Termination
Upon termination:
– Your right to use the Platform/Service ceases immediately
– We may delete your account and data after a reasonable retention period
– Provisions that by nature should survive termination shall remain in effect`
    },
    {
      title: "13. GOVERNING LAW AND DISPUTE RESOLUTION",
      content: `13.1 Governing Law
These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law principles.

13.2 Jurisdiction
Any disputes arising out of or relating to these Terms or the Platform/Service shall be subject to the exclusive jurisdiction of the courts in Mumbai, Maharashtra, India.

13.3 Alternative Dispute Resolution
Before initiating formal legal proceedings, parties agree to attempt resolution through good faith negotiations. If unsuccessful, disputes may be resolved through arbitration under the Arbitration and Conciliation Act, 2015.`
    },
    {
      title: "14. INTERNATIONAL USERS",
      content: `14.1 Global Access
While our Platform/Service is available globally, users outside India acknowledge that:
– Data may be transferred to and processed in India
– Indian laws govern the Platform/Service and these Terms
– Some features may not be available in all jurisdictions

14.2 Compliance with Local Laws
International users are responsible for compliance with their local laws and regulations regarding the use of our Platform/Service.`
    },
    {
      title: "15. MODIFICATIONS TO TERMS",
      content: `We reserve the right to modify these Terms at any time. We will notify users of material changes via email or through the Platform/Service. Continued use of the Platform/Service after changes constitutes acceptance of the modified Terms.`
    },
    {
      title: "16. MISCELLANEOUS",
      content: `16.1 Entire Agreement
These Terms constitute the entire agreement between you and Decks N Dollars LLP regarding the Platform/Service.

16.2 Severability
If any provision of these Terms is found to be unenforceable, the remaining provisions shall remain in full force and effect.

16.3 Waiver
Our failure to enforce any provision of these Terms shall not constitute a waiver of that provision.

16.4 Assignment
We may assign these Terms or any of our rights hereunder without your consent. You may not assign these Terms without our prior written consent.`
    },
    {
      title: "17. CONTACT INFORMATION",
      content: `For questions about these Terms, please contact us at:

Decks N Dollars LLP
Email: legal@decx.ai
Phone: +91 9987 646 054
Address: A 201, Shetrunji Apt, Sahakargram Complex, Ashok Nagar, Kandivali East
Mumbai, Maharashtra, India`
    }
  ];

  return (
    <div data-testid="terms-page">
      <PageHero eyebrow="LEGAL" title="Terms of" accent="Platform/Service" />
      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Last Updated */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '40px',
            fontSize: '14px',
            opacity: 0.7,
            fontFamily: "'Manrope', sans-serif"
          }}>
            Last Updated: 04-Mar-2026
          </div>

          {/* Terms Sections */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {sections.map((section, index) => (
              <Crd key={index} style={{ padding: '28px 32px' }}>
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: 700, 
                  marginBottom: '16px', 
                  fontFamily: "'Sansation', sans-serif",
                  color: C.secondary
                }}>
                  {section.title}
                </h3>
                <div 
                  className="font-[Manrope]" 
                  style={{ 
                    lineHeight: 1.8, 
                    fontSize: '14px',
                    whiteSpace: 'pre-line',
                    opacity: 0.85
                  }}
                >
                  {section.content}
                </div>
              </Crd>
            ))}
          </div>
        </div>
      </Sec>
    </div>
  );
};

export const RefundPage = () => {
  const { theme } = useTheme();

  return (
    <div data-testid="refund-page">
      <PageHero eyebrow="LEGAL" title="Refund" accent="Policy" />
      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <Crd>
            <p className="font-[Manrope]" style={{ lineHeight: 1.8, fontSize: '15px' }}>
              Decks N Dollars LLP ("DecX AI") offers the following refund policy: The free DecX Score is provided at no cost — no refund applicable. Credit packages (Starter, Growth, Scale) — unused credits may be refunded within 7 days of purchase. Once credits are consumed for analysis, they are non-refundable. Slidr DFY services — 50% deposit is non-refundable once work begins. For refund requests, contact legal@decx.ai within the applicable timeframe. Decks N Dollars LLP reserves the right to modify this policy.
            </p>
          </Crd>
        </div>
      </Sec>
    </div>
  );
};

// Hub Pages - Exact original content
export const BlogPage = () => {
  const { theme } = useTheme();

  return (
    <div data-testid="blog-page">
      <PageHero
        eyebrow="BLOG"
        title="Deck Intelligence"
        accent="Blog"
        subtitle="Insights on pitch decks, sales enablement, and closing strategies."
      />
      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <Crd>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🚀</div>
            <h3 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px', fontFamily: "'Sansation', sans-serif" }}>
              Coming Soon
            </h3>
            <p className="font-[Manrope]" style={{ fontSize: '15px', opacity: 0.75 }}>
              Valuable content on deck strategy, fundraising, and sales enablement.
            </p>
          </Crd>
        </div>
      </Sec>
    </div>
  );
};

export const NewsletterPage = () => {
  const { theme } = useTheme();

  return (
    <div data-testid="newsletter-page">
      <PageHero
        eyebrow="NEWSLETTER"
        title="Decks N Dollars"
        accent="Newsletter"
        subtitle="Weekly insights on decks, fundraising, and sales."
      />
      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <Crd>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🚀</div>
            <h3 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px', fontFamily: "'Sansation', sans-serif" }}>
              Coming Soon
            </h3>
            <p className="font-[Manrope]" style={{ fontSize: '15px', opacity: 0.75 }}>
              Valuable content on deck strategy, fundraising, and sales enablement.
            </p>
          </Crd>
        </div>
      </Sec>
    </div>
  );
};

export const ResourcesPage = () => {
  const { theme } = useTheme();

  return (
    <div data-testid="resources-page">
      <PageHero
        eyebrow="RESOURCES"
        title="Guides &"
        accent="Resources"
        subtitle="Templates, frameworks, and lead magnets."
      />
      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <Crd>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🚀</div>
            <h3 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px', fontFamily: "'Sansation', sans-serif" }}>
              Coming Soon
            </h3>
            <p className="font-[Manrope]" style={{ fontSize: '15px', opacity: 0.75 }}>
              Valuable content on deck strategy, fundraising, and sales enablement.
            </p>
          </Crd>
        </div>
      </Sec>
    </div>
  );
};

// LLMs Page - Exact original content
export const LLMsPage = () => {
  const { theme } = useTheme();

  const llmsTxt = `# DecX AI — LLMs.txt
# https://decx.ai/.well-known/llms.txt

> DecX AI is a deck intelligence platform that transforms pitch, sales, and product decks into closing machines.
> The power that drives closure.

## About
- Name: DecX AI
- URL: https://decx.ai
- Category: B2B SaaS, Deck Intelligence, Presentation Analytics
- Founded: 2025
- Founder: Neeraj P
- Entity: Decks N Dollars LLP
- Contact: win@decx.ai

## Core Offering
- DecX Score: AI-powered deck scoring across 5 dimensions (Credibility, Clarity, Market Fit, Competitive Edge, Presentation)
- Audience Insights: Persona mapping for investors, buyers, executives
- Industry Trends: Sector-specific benchmarks
- Closing Artefacts: Pitch Canvas, Deck Rebuild Prompts, Outreach Sequences, Brand Videos
- Strategy Calls: 1:1 expert sessions
- Slidr DFY: Done-for-you deck creation starting at $779

## Pricing
- Free: Basic DecX Score (1 deck, 5 dimensions, 24hr delivery)
- Starter: $49 (10 credits)
- Growth: $199 (50 credits, includes strategy call)
- Scale: $499 (150 credits, team access)
- Enterprise: Custom pricing

## Use Cases
- Founders raising capital (pitch decks)
- B2B sales teams (sales decks)
- Product managers (stakeholder buy-in)
- Accelerators (portfolio scoring)

## Key Stats
- 1,000+ decks analyzed
- $500M+ raised by users
- 40%+ close rate improvement for sales teams
- 50%+ faster approval cycles for PMs`;

  const geoKeywords = {
    'Intelligence-Focused': ['deck intelligence platform', 'pitch deck intelligence', 'sales deck intelligence', 'AI deck scoring', 'deck effectiveness platform', 'presentation intelligence software'],
    'Outcome-Focused': ['close more deals with decks', 'win investor meetings', 'deck that converts', 'get investor yes', 'close B2B deals faster', 'close funding round faster'],
    'Geographic': ['deck intelligence India', 'pitch deck software Mumbai', 'startup deck intelligence USA', 'deck intelligence Dubai', 'pitch deck platform Singapore', 'deck scoring UK London'],
    'Long-Tail': ["why investors aren't responding to my deck", 'improve B2B sales deck close rate', 'get budget approval for product roadmap', 'how to close seed round faster', 'pitch deck that gets meetings']
  };

  return (
    <div data-testid="llms-page">
      <PageHero
        eyebrow="AI OPTIMIZATION"
        title="LLMs.txt &"
        accent="GEO Strategy"
        subtitle="Optimized for AI search engines and LLM discovery."
        body="DecX AI is built for the age of generative search. Our content is structured for AI discoverability across ChatGPT, Perplexity, Gemini, and more."
      />

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <SH
            eyebrow="LLMs.txt"
            title="Machine-Readable"
            accent="Platform Summary"
            center
          />
          <Crd style={{ marginTop: '32px' }}>
            <pre className="font-[Manrope]" style={{ fontSize: '13px', lineHeight: 1.7, opacity: 0.9, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {llmsTxt}
            </pre>
          </Crd>
        </div>
      </Sec>

      <Sec dark={theme === 'light'}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <SH
            eyebrow="GEO STRATEGY"
            title="Generative Engine"
            accent="Optimization Keywords"
            center
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px', marginTop: '48px' }}>
            {Object.entries(geoKeywords).map(([category, keywords], i) => (
              <Crd key={i}>
                <h4 style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '16px' }}>
                  {category}
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {keywords.map((kw, j) => (
                    <span key={j} style={{ fontSize: '13px', opacity: 0.8 }}>• {kw}</span>
                  ))}
                </div>
              </Crd>
            ))}
          </div>
        </div>
      </Sec>
    </div>
  );
};
